import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { isChunkLoadError } from './ChunkLoadErrorBoundary';
import { Button } from '@/components/ui/Button';

/**
 * Top-level recoverable error boundary for render-time throws.
 *
 * Sits INSIDE [ChunkLoadErrorBoundary] (which owns the post-deploy chunk
 * reload). This one catches everything else — a component that throws while
 * rendering now shows an actionable fallback instead of the misleading
 * "new version, reload" chunk copy (or a white screen). Chunk-load errors
 * are re-thrown so the outer boundary still handles them with its reload.
 *
 * Recovery: "Try again" resets local state and re-renders the subtree (good
 * for a transient render error); "Reload" hard-reloads as the fallback.
 */
interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  private reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (error) {
      // Defer chunk-load failures to the outer ChunkLoadErrorBoundary.
      if (isChunkLoadError(error)) throw error;
      return <ErrorFallback onReset={this.reset} />;
    }
    return this.props.children;
  }
}

// Hook-using functional component so the class boundary can still render
// translated copy.
function ErrorFallback({ onReset }: { onReset: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="bg-page text-text-primary flex min-h-screen items-center justify-center p-8">
      <div className="max-w-md text-center">
        <h1 className="font-heading text-2xl font-bold">{t('errors.unexpectedTitle')}</h1>
        <p className="text-text-secondary mt-2 text-sm">{t('errors.unexpectedHelp')}</p>
        <div className="mt-6 flex items-center justify-center gap-3">
          <Button onClick={onReset}>{t('errors.tryAgain')}</Button>
          <Button variant="ghost" onClick={() => window.location.reload()}>
            {t('errors.reload')}
          </Button>
        </div>
      </div>
    </div>
  );
}
